<template>
  <div
    v-on-clickaway="close"
    class="filter"
    :class="{ 'filter--selected': !!selected }"
  >
    <EButton
      ref="button"
      :icon="icon"
      size="s"
      icon-position="end"
      class="filter__button"
      :class="{ 'filter__button--highlighted': buttonHighlighted }"
      data-test-id="filter-button"
      role="menuitem"
      aria-haspopup="true"
      variant="bare"
      @click="onClick"
    >
      <EColorIndicator
        v-if="selectedColor"
        :color="selectedColor"
        :show-plus="false"
        size="s"
        class="filter__button-color"
        :class="`filter__button-color--${selected}`"
        data-test-id="filter-button-color"
      />
      <span class="filter__text">
        <slot name="text">
          {{ text }}
        </slot>
      </span>
    </EButton>
    <EModalDropdown
      v-accessible-menu
      :title="hint"
      :visible="isOpen"
      :size="size"
      :aria-label="hint"
      :side="side"
      class="filter__content"
      role="menu"
      @close="close"
    >
      <slot>
        <header
          v-if="!selectedColor && !isTypeSelect"
          class="filter__selection"
          data-test-id="filter-selection"
        >
          {{ text }}
          <EIcon
            class="filter__selection-icon"
            icon="check"
            size="s"
          />
        </header>
        <EList role="none">
          <EListItem
            v-for="({ value, label }) in filteredOptions"
            :key="value"
            :data-test-id="`filter-option-${value}`"
            :data-track-filter="`${name}:${value}`"
            data-track-id="filter-option"
            class="filter__item"
            :class="{ 'filter__item--highlighted': isTypeSelect && value === selected }"
            role="menuitem"
            @click="onSelect(value)"
          >
            {{ label }}
            <template #right>
              <EIcon
                v-if="isTypeSelect && value === selected"
                class="filter__selection-icon"
                icon="check"
                size="s"
              />
            </template>
          </EListItem>
        </EList>
        <EList v-if="isTypeColor" role="none" class="filter__colors">
          <li
            v-for="({ value, label, color }) in colorOptions"
            :key="value"
            class="filter__color"
            :class="`filter__color--${value}`"
            :data-test-id="`filter-option-color-${value}`"
            :data-track-filter="`${name}:${value}`"
            data-track-id="filter-option"
            @keypress.enter.space.prevent="onSelect(value)"
            @click="onSelect(value)"
          >
            <EColorIndicator
              :color="color"
              :active="value === selected"
              role="menuitem"
              class="filter__color-indicator"
              :aria-label="label"
              :title="label"
              :dark-icons="value === 'white'"
              :size="colorIndicatorSize"
            />
          </li>
        </EList>
      </slot>
    </EModalDropdown>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import screen from '~~/common/vue/mixins/screen';
import EColorIndicator from '~~/common/vue/components/color-indicator';
import EIcon from '~~/common/vue/components/icon';
import EList from '~~/common/vue/components/list';
import EListItem from '~~/common/vue/components/list-item';

import EButton from '~~/common/vue/components/button';
import EModalDropdown from '~~/common/vue/components/modal-dropdown';

export const types = ['default', 'color', 'select'];
/**
 * A filter control with a modal-dropdown
 */
export default {
  name: 'EFilter',
  components: {
    EButton,
    EColorIndicator,
    EIcon,
    EList,
    EListItem,
    EModalDropdown,
  },
  mixins: [clickaway, screen('debounced', 100)],
  props: {
    // eslint-disable-next-line vue/require-default-prop
    size: EModalDropdown.props.size,
    side: {
      type: String,
      default: 'center',
    },
    name: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'default',
      validator: (value) => types.includes(value),
    },
    hint: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    selected: {
      type: [String, null],
      default: null,
    },
    highlightOnSelect: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isOpen: false,
  }),
  computed: {
    isTypeColor() {
      return this.type === 'color';
    },
    isTypeSelect() {
      return this.type === 'select';
    },
    filteredOptions() {
      let { options } = this;
      if (this.isTypeColor) {
        options = options.filter((option) => !this.isColorOption(option));
      }
      if (this.selected && this.placeholder) {
        options = options.concat([{ value: '', label: this.placeholder }]);
        options = options.filter(({ value }) => value !== this.selected);
      }
      return options;
    },
    colorOptions() {
      return this.isTypeColor ? this.options.filter(this.isColorOption.bind(this)) : null;
    },
    icon() {
      return this.isOpen ? 'chevron-mini-up' : 'chevron-mini-down';
    },
    selectedText() {
      if (!this.selected) {
        return '';
      }
      const option = this.options.find(({ value }) => value === this.selected);
      return option ? option.label : '';
    },
    selectedColor() {
      if (!(this.isTypeColor && this.selected)) {
        return null;
      }
      const color = this.colorOptions.find(({ value }) => value === this.selected);
      return color ? color.color : null;
    },
    text() {
      return this.selectedText || this.placeholder;
    },
    colorIndicatorSize() {
      return this.isDesktopUp ? 's' : 'm';
    },
    buttonHighlighted() {
      return this.highlightOnSelect && (this.isOpen || this.selected);
    },
  },
  methods: {
    isColorOption({ color }) {
      return !!color;
    },
    onClick() {
      this.isOpen = !this.isOpen;
    },
    onSelect(option) {
      this.$emit('select', option);
      this.isOpen = false;
    },
    close() {
      if (this.isOpen) {
        this.$refs.button.$el.focus();
      }
      this.isOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
$filter-color-wrapper-width-desktop: 164px;

.filter {
  position: relative;
}

.filter--selected .filter__button--highlighted .filter__text,
.filter__item--highlighted {
  font-weight: $font-weight-1l;
}

.filter__selection {
  align-items: center;
  display: flex;
  font-size: $font-m;
  font-weight: $font-weight-1l;
  justify-content: space-between;
  margin-right: $space-1s;
  padding: 0 $space-m;
  position: relative;
  white-space: nowrap;
  height: $tappable-area-size-mobile;

  @include desktop {
    margin-right: 0;
    height: $space-3l;
  }
}

.filter__selection-icon {
  margin-left: $space-1s;
}

.filter__item,
.filter__selection {
  box-sizing: content-box;
  padding-left: $space-m;

  @include desktop {
    font-size: $font-m;
    padding-left: $space-s;
  }
}

.filter__item {
  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.filter__button-color {
  margin-right: $space-1s;
}

.filter__content {
  @include desktop {
    top: calc(100% + #{$space-2s});
  }
}

.filter__colors {
  align-self: center;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  padding: $space-m $space-s $space-s $space-s;
  margin-bottom: $space-1s;

  @include desktop {
    padding: 0 $space-3s $space-2s $space-3s;
    margin: 0;
    min-width: $filter-color-wrapper-width-desktop;
  }
}

.filter__color {
  display: inline-flex;
  cursor: pointer;
  padding: $space-s;
  transition: box-shadow $timing-2s $easing;
}

.filter__button-color--white,
.filter__color--white .filter__color-indicator {
  // White color needs a darker border
  box-shadow: 0 0 1px $black;
}

.filter__button-color--black,
.filter__color--black .filter__color-indicator {
  // Black color needs a light border on dark themes
  box-shadow: 0 0 1px $white;
}
</style>
